import { useMeasurementUnit } from 'hooks/useMeasurementUnit';
import { QuestionCircleTwoTone } from '@ant-design/icons';
import { useMemoizedFn, useMount } from 'ahooks';
import {
  Affix,
  Divider,
  Input,
  PageHeader,
  Radio,
  Select,
  Space,
  Switch,
  Tooltip,
} from 'antd';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { ArrowLeftIcon, EditIcon } from 'components/Icons';
import { RevxButton } from 'components/RevxButton';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { success } from 'components/RevxMessage';
import { Headline } from 'components/RevxTypography';
import {
  InheritSettingEnum,
  MultiTypeEnum,
  PartialSKU,
  SKU,
  SKUPart,
} from 'domains/skuDomain';
import { useSkuHideSaveAsNew } from 'hooks/useSkuHideSaveAsNew';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import skuService from 'services/skuService';
import { useImmer } from 'use-immer';
import { ADMIN_DETAIL_AFFIX_TOP, NAME_MAX_LENGTH } from 'utils/constants';
import { computeSkuDiffs, DiffInfo, parcelCategoriesList } from 'utils/review';
import { computeDiff } from './components/DiffButton';
import { DirectlyAddToCart } from './components/DirectlyAddToCart';
import { GalleryImage } from './components/GalleryImage';
import { GalleryLabel } from './components/GalleryLabel';
import { PartsTable } from './components/PartsTable';
import { SkuDisplayName } from './components/SkuDisplayName';
import MultiSkuConfigWizard from './MultiSkuConfigWizard';
import './MultiSkuDetail.less';
import { SkuPublishReview } from './SkuPublishReview';
import { SpecifySuppliersFlag } from './components/SpecifySuppliers';

type Props = { sku: PartialSKU };

const MultiSkuDetail = ({ sku }: Props) => {
  // Required by computeSkuDiffs, and in the memo's deps, so the diff re-renders on a switch.
  const { unit } = useMeasurementUnit();
  const { t } = useTranslation();
  const curItem = sku;

  const dispatch = useAppDispatch();
  const [wizardVisible, setWizardVisible] = useState(false);
  const [publishVisible, setPublishVisible] = useState(false);
  const skuTypes = useAppSelector((state) => state.app.skuTypes);
  const needSaveDraft = useAppSelector((state) => state.sku.needSaveDraft);
  const [mainSku, setMainSku] = useState<SKU>();
  const eqptTypes = useAppSelector((state) => state.eqpt.eqptTypes);
  const [partDiffMap, setPartDiffMap] = useImmer<{
    [index: string]: { diffs: DiffInfo[]; skuPart: SKUPart };
  }>({});

  const hideSaveAsNew = useSkuHideSaveAsNew(mainSku);

  useEffect(() => {
    curItem.parts?.forEach(async (part) => {
      const diffs = await computeDiff(
        part.part,
        eqptTypes,
        mainSku?.parts
          .map((item) => item.part)
          .find((item) => part.part.draftFromId === item.id)
      );

      setPartDiffMap((draft) => {
        draft[part.id] = {
          diffs,
          skuPart: part,
        };
      });
    });
  }, [curItem.parts, eqptTypes, mainSku?.parts, setPartDiffMap]);

  const onSaveAsNew = useMemoizedFn(async () => {
    const draftSku = await skuService.createSku({
      isMulti: Boolean(mainSku?.multiType === MultiTypeEnum.WRAPPER),
      templateId: mainSku?.id,
    });

    dispatch.sku.setState({
      curItem: draftSku,
    });
    dispatch.sku.refresh();

    success(t('platform.sku.createSuccess'));

    // defaultSkuHandle();
    // onClose();
  });

  const partDiffs = useMemo(() => {
    const partDiffValues = Object.values(partDiffMap);

    return partDiffValues
      .map((partDiff) => {
        return partDiff.diffs;
      })
      .flat();
  }, [partDiffMap]);

  const onClose = () => {
    dispatch.sku.setState({ curItem: undefined });
    dispatch.sku.refresh();
  };

  useMount(async () => {
    document.documentElement.scrollTop = 0;
    const main = await skuService.startViewSku(curItem);
    dispatch.sku.setMultiStep(0);
    setMainSku(main);
  });

  const updateCurItem = async (item: PartialSKU) => {
    await dispatch.sku.updateCurItemForSave(item);
    success(t('platform.sku.changesSavedTip'));
  };
  const updateCurItemForLaterSave = async (item: PartialSKU) => {
    await dispatch.sku.updateCurItemForLaterSave(item);
  };

  const diffs = useMemo(() => {
    return computeSkuDiffs(sku, mainSku, [], [], [], true, unit);
  }, [mainSku, sku, unit]);

  const parcelCategoriesError = useMemo(() => {
    return Boolean(
      (!sku?.parcelCategories || !sku?.parcelCategories?.length) &&
        !curItem?.readonly
    );
  }, [curItem, sku]);

  const galleryImageError = useMemo(() => {
    return !curItem.images?.[0] && !curItem.readonly && !!curItem.active;
  }, [curItem]);

  const topGalleryLabelError = useMemo(() => {
    if (!curItem.isBaseSku) return false;
    return (
      (!curItem.topGalleryLabels || !curItem.topGalleryLabels.length) &&
      curItem.multiType !== MultiTypeEnum.PART &&
      !curItem.readonly &&
      !!curItem.active
    );
  }, [curItem]);

  const galleryLabelError = useMemo(() => {
    if (!curItem.isBaseSku) return false;
    return (
      !curItem?.galleryLabel?.id &&
      curItem.multiType !== MultiTypeEnum.PART &&
      !curItem.readonly &&
      !!curItem.active
    );
  }, [curItem]);

  const companyLabelError = useMemo(() => {
    return (
      !curItem?.companyLabel?.id &&
      curItem.multiType !== MultiTypeEnum.PART &&
      !curItem.readonly &&
      !!curItem.active
    );
  }, [curItem]);

  const reviewChangesDisabled = useMemo(() => {
    return Boolean(
      (diffs.length === 0 && partDiffs.length === 0) ||
        galleryImageError ||
        topGalleryLabelError ||
        galleryLabelError ||
        parcelCategoriesError
    );
  }, [
    diffs,
    partDiffs,
    galleryImageError,
    topGalleryLabelError,
    galleryLabelError,
    parcelCategoriesError,
  ]);

  const actionBar = (
    <div>
      <Affix offsetTop={ADMIN_DETAIL_AFFIX_TOP}>
        <PageHeader
          className="platform-item-form-header"
          onBack={() => {
            onClose();
          }}
          backIcon={<ArrowLeftIcon />}
          title={
            <div style={{ display: 'flex' }}>
              {!curItem.readonly && (
                <Headline key="1" style={{ width: 'auto' }}>
                  [{t('platform.sku.draft')}]
                </Headline>
              )}
              <Headline
                ellipsis={{
                  tooltip: curItem.name,
                }}
                style={{ marginTop: 0, marginLeft: 10 }}
                editable={
                  curItem.readonly
                    ? undefined
                    : {
                        ...(curItem.name ? {} : { editing: true }),
                        maxLength: NAME_MAX_LENGTH,
                        onStart: () => {
                          updateCurItemForLaterSave({
                            ...curItem,
                          });
                        },
                        onChange: (value) => {
                          if (value !== curItem.name) {
                            updateCurItem({
                              ...curItem,
                              name: value,
                            });
                          }
                        },
                        icon: <EditIcon />,
                      }
                }
              >
                {curItem.name}
              </Headline>
            </div>
          }
          extra={
            curItem.readonly
              ? [
                  <RevxButton
                    onClick={onSaveAsNew}
                    brandGreen
                    type="primary"
                    key="onSaveAsNew"
                    hidden={hideSaveAsNew}
                  >
                    {t('common.saveAsNew')}
                  </RevxButton>,
                  <RevxButton onClick={onClose} brandGreen key="cancel">
                    {t('common.cancel')}
                  </RevxButton>,
                ]
              : [
                  <RevxButton
                    type="primary"
                    htmlType="submit"
                    key="publish"
                    disabled={reviewChangesDisabled}
                    onClick={() => {
                      setPublishVisible(true);
                    }}
                    brandGreen
                  >
                    <Tooltip
                      title={
                        diffs.length > 0 || partDiffs.length > 0
                          ? ''
                          : t('platform.sku.publishReview.onChangesFound')
                      }
                    >
                      {t('platform.sku.publishReview.reviewChanges')}
                    </Tooltip>
                  </RevxButton>,
                ]
          }
        ></PageHeader>
      </Affix>
    </div>
  );

  return (
    <>
      <div
        className="multi-sku-detail"
        hidden={wizardVisible || publishVisible}
      >
        {actionBar}
        <div className="multi-sku-detail-form">
          <div className="multi-sku-detail-form-row">
            <Space>
              <label>{t('platform.sku.form.label.active')}:</label>
              <Switch
                checked={curItem.active}
                disabled={curItem.readonly}
                onChange={(val) => {
                  updateCurItem({
                    ...curItem,
                    active: val,
                  });
                }}
              />
              <Divider type="vertical" />
              <label>{t('platform.sku.form.label.skuType')}:</label>
              <Select
                style={{ width: 200 }}
                showSearch
                disabled={curItem.readonly || !!curItem.skuType}
                value={curItem?.skuType?.id}
                filterOption={(input, option) =>
                  option?.children
                    ?.toLowerCase()
                    ?.indexOf(input?.toLowerCase()) >= 0
                }
                onChange={(val) => {
                  updateCurItem({
                    ...curItem,
                    skuType: skuTypes.find((t) => t.id === val),
                  });
                }}
                getPopupContainer={(e) => {
                  return e as HTMLElement;
                }}
              >
                {skuTypes.map((skuType) => {
                  return (
                    <Select.Option value={skuType.id} key={skuType.id}>
                      {skuType.name}
                    </Select.Option>
                  );
                })}
              </Select>
              <div
                hidden={!!curItem.skuType || curItem.readonly}
                className="ant-form-item-explain-error"
                style={{
                  position: 'absolute',
                  left: 204,
                  top: 35,
                }}
              >
                {t('platform.sku.skuTypeRequired')}
              </div>
              <Divider type="vertical" />
              <label>{t('platform.sku.form.label.taxCode')}:</label>
              <Input
                disabled={curItem.readonly}
                value={curItem.taxCode}
                onChange={(e) => {
                  if (e.target.value !== curItem.taxCode) {
                    updateCurItemForLaterSave({
                      ...curItem,
                      taxCode: e.target.value,
                    });
                  }
                }}
                onBlur={(e) => {
                  if (e.target.value !== curItem.taxCode || needSaveDraft) {
                    updateCurItem({
                      ...curItem,
                      taxCode: e.target.value,
                    });
                  } else {
                    dispatch.sku.setState({
                      needSaveDraft: false,
                    });
                  }
                }}
              />
              <a
                href="https://taxcode.avatax.avalara.com/"
                target="_blank"
                rel="noreferrer"
              >
                <QuestionCircleTwoTone />
              </a>
              <Divider type="vertical" />
              <label>{t('platform.sku.form.label.parcelCategories')}:</label>
              <div style={{ position: 'relative' }}>
                <Select
                  mode="multiple"
                  style={{ minWidth: 200 }}
                  showSearch
                  disabled={sku.readonly}
                  value={sku?.parcelCategories || undefined}
                  filterOption={(input, option) =>
                    Boolean(
                      (parcelCategoriesList || [])
                        .find((item) => item?.value === option?.value)
                        ?.title?.toLowerCase()
                        ?.includes(input?.toLowerCase())
                    )
                  }
                  onChange={(val) => {
                    updateCurItem({
                      ...sku,
                      parcelCategories: val,
                    });
                  }}
                  getPopupContainer={(e) => {
                    return e as HTMLElement;
                  }}
                  dropdownStyle={{
                    zIndex: 4,
                  }}
                >
                  {parcelCategoriesList.map((parcelCategorie) => {
                    return (
                      <Select.Option
                        value={parcelCategorie.value}
                        key={parcelCategorie.value}
                      >
                        <span>{parcelCategorie.title}</span>
                      </Select.Option>
                    );
                  })}
                </Select>

                <div
                  hidden={!parcelCategoriesError}
                  className="ant-form-item-explain-error"
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 35,
                  }}
                >
                  {t('platform.sku.parcelCategoriesRequired')}
                </div>
              </div>
            </Space>
          </div>
          <GalleryLabel
            showTopGalleryLabelsError={topGalleryLabelError}
            showGalleryLabelError={galleryLabelError}
            showCompanyLabelError={companyLabelError}
            sku={curItem}
            updateCurItem={updateCurItem}
          />
          <SkuDisplayName sku={curItem} updateCurItem={updateCurItem} />
          <RevxFlexLayout
            style={{ marginTop: 20 }}
            justify="space-between"
            align="top"
          >
            <GalleryImage sku={curItem} showError={galleryImageError} />

            <div className="multi-sku-config-options">
              <Headline style={{ marginBottom: 20 }}>
                <RevxFlexLayout>
                  <span>{t('platform.sku.form.label.configOptions')}</span>
                  <RevxButton
                    brandGreen
                    type="primary"
                    size="small"
                    style={{ marginLeft: 20 }}
                    onClick={() => setWizardVisible(true)}
                    disabled={!Boolean(curItem?.skuType?.id)}
                  >
                    {t('platform.sku.form.label.goNow')}
                  </RevxButton>
                </RevxFlexLayout>
              </Headline>
              <div
                className="multi-sku-detail-form-row"
                hidden={curItem?.isBaseSku}
              >
                <label>{t('platform.sku.form.label.inheritUpdates')}:</label>
                <Radio.Group
                  value={curItem?.inheritSetting}
                  disabled={curItem.readonly}
                  onChange={(e) => {
                    updateCurItem({
                      ...curItem,
                      inheritSetting: e.target.value as InheritSettingEnum,
                    });
                  }}
                >
                  <Radio value={InheritSettingEnum.EXCEPTION_AUTO}>
                    {t('platform.sku.form.label.exceptionAuto')}
                  </Radio>
                  <Radio value={InheritSettingEnum.EXCEPTION_MANUAL}>
                    {t('platform.sku.form.label.exceptionManual')}
                  </Radio>
                  <Radio value={InheritSettingEnum.NO_EXCEPTION}>
                    {t('platform.sku.form.label.noException')}
                  </Radio>
                </Radio.Group>
              </div>
              <div
                className="multi-sku-detail-form-row"
                hidden={curItem?.multiType === MultiTypeEnum.PART}
              >
                <label>{t('platform.sku.form.label.hideFreightCost')}</label>
                <Radio.Group
                  value={Boolean(curItem?.hideFreightCost)}
                  disabled={curItem.readonly}
                  onChange={(e) => {
                    updateCurItem({
                      ...curItem,
                      hideFreightCost: e.target.value,
                    });
                  }}
                >
                  <Radio value={true}>{t('common.yes')}</Radio>
                  <Radio value={false}>{t('common.no')}</Radio>
                </Radio.Group>
              </div>
              <DirectlyAddToCart
                curItem={curItem}
                updateCurItem={updateCurItem}
              />
              <div className="bold mb-20">
                <SpecifySuppliersFlag sku={sku} />
              </div>
              <div className="multi-sku-overview">
                <PartsTable
                  parts={curItem.parts || []}
                  sku={curItem}
                  onConfig={(skuPart) => {
                    dispatch.sku.setState({
                      curItem: skuPart,
                      curTopItem: sku,
                    });
                  }}
                />
              </div>
            </div>
          </RevxFlexLayout>
        </div>
      </div>
      {wizardVisible && (
        <MultiSkuConfigWizard
          sku={curItem}
          onCancel={() => {
            setWizardVisible(false);
          }}
        />
      )}
      {publishVisible && mainSku && (
        <SkuPublishReview
          hidePublishBtn={false}
          exceptEqpts={[]}
          mainExceptEqpts={[]}
          eqptTypes={[]}
          sku={curItem}
          mainSku={mainSku}
          partDiffMap={partDiffMap}
          isMulti={true}
          onClose={async () => {
            setPublishVisible(false);
            const main = await skuService.startViewSku(curItem);
            setMainSku(main);
          }}
        />
      )}
    </>
  );
};
export default MultiSkuDetail;
